import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useI18n } from "../lib/i18n";
import { fmtEur, fmtDate } from "../lib/utils";
import {
  Wallet, Warning, ArrowRight, Package, ChartPieSlice, TrendUp, Globe,
  HandWaving, Sparkle, Lightning, Truck, Handbag, Trophy, Medal,
  Confetti, ShieldCheck, Star, Fire, Crown, Lock, Plus, CheckCircle,
  CreditCard, Calendar, Receipt, DownloadSimple,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { celebrateNewlyUnlocked } from "../lib/achievements";
import WelcomeSplash from "../components/WelcomeSplash";
import SetupChecklist from "../components/SetupChecklist";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Cell,
  PieChart, Pie,
} from "recharts";

function ChartBarTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[var(--tf-ink)] text-white rounded-xl px-3 py-2 text-xs shadow-lg">
      <div className="font-bold">{label}</div>
      <div className="tf-numeric">{fmtEur(payload[0].value)}</div>
    </div>
  );
}

const COLORS = {
  pod: "#CCF22C",
  shipping: "#193529",
  picking: "#D4A017",
};

const LOCALE_MAP = { it: "it-IT", en: "en-GB", fr: "fr-FR", de: "de-DE", es: "es-ES" };

export default function ClientDashboard() {
  const { t, lang } = useI18n();
  const locale = LOCALE_MAP[lang] || "it-IT";
  const [me, setMe] = useState(null);
  const [orders, setOrders] = useState([]);
  const [allOrders, setAllOrders] = useState([]);
  const [txs, setTxs] = useState([]);
  const [allTxs, setAllTxs] = useState([]);
  const [stats, setStats] = useState(null);
  const [period, setPeriod] = useState(30);
  const [autoRecharge, setAutoRecharge] = useState(null);
  const [feedFilter, setFeedFilter] = useState("all"); // "all" | "recharges"
  const [topupBusy, setTopupBusy] = useState(false);
  const [customAmount, setCustomAmount] = useState("");

  useEffect(() => {
    const load = async () => {
      const [m, o, x, ar] = await Promise.all([
        api.get("/client/me"),
        api.get("/client/orders"),
        api.get("/client/transactions"),
        api.get("/client/auto-recharge").catch(() => ({ data: null })),
      ]);
      setMe(m.data);
      setAllOrders(o.data);
      setOrders(o.data.slice(0, 5));
      setAllTxs(x.data);
      setTxs(x.data.slice(0, 5));
      setAutoRecharge(ar.data);
    };
    load();
  }, []);

  useEffect(() => {
    api.get(`/client/stats?days=${period}`).then(({ data }) => setStats(data));
  }, [period]);

  const quickTopUp = async (amt) => {
    if (topupBusy) return;
    const amount = parseFloat(amt);
    if (!amount || amount < 50) { toast.error(t("dash_min_amount_error")); return; }
    setTopupBusy(true);
    try {
      const { data } = await api.post("/payments/checkout/session", {
        amount,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("dash_stripe_error"));
      setTopupBusy(false);
    }
  };

  const daily7 = useMemo(() => {
    if (!stats?.daily) return [];
    const last = stats.daily.slice(-7);
    return last.map((d) => ({
      day: new Date(d.date).toLocaleDateString(locale, { weekday: "narrow" }),
      value: (d.pod || 0) + (d.shipping || 0) + (d.picking || 0),
    }));
  }, [stats, locale]);
  const topBar = daily7.length ? daily7.reduce((a, b) => (b.value > a.value ? b : a), daily7[0]) : null;

  // Unified feed: merge orders + transactions sorted by date desc (hooks must run every render)
  const unifiedFeed = useMemo(() => {
    const orderItems = allOrders.slice(0, 30).map((o) => ({
      key: `o-${o.id}`,
      kind: "order",
      date: o.myze_created_at || o.created_at,
      label: o.origin_id || o.external_order_number || o.myze_order_id || t("orders"),
      subtitle: o.shipping_country ? t("feed_destination", { country: o.shipping_country }) : "",
      amount: o.cost?.total || 0,
      direction: "debit",
      status: o.status,
    }));
    const txItems = allTxs.slice(0, 30).map((x) => ({
      key: `t-${x.id}`,
      kind: x.type === "auto_recharge" ? "auto_recharge" : (x.direction === "credit" ? "recharge" : "debit_tx"),
      date: x.created_at,
      label: x.description || x.type,
      subtitle: x.fic_proforma?.number ? `Proforma n.${x.fic_proforma.number}` : "",
      amount: x.amount,
      direction: x.direction,
      tx: x,
    }));
    const combined = [...orderItems, ...txItems].sort((a, b) => new Date(b.date) - new Date(a.date));
    if (feedFilter === "recharges") return combined.filter((i) => i.direction === "credit");
    return combined.slice(0, 15);
  }, [allOrders, allTxs, feedFilter, t]);

  if (!me) return <div className="p-8 text-center text-[var(--tf-muted)]">{t("loading")}</div>;

  const firstName = (me.user?.name || me.client?.contact_name || me.client?.company_name || "").split(" ")[0] || "";
  const balance = me.wallet.balance;
  const threshold = me.low_credit_threshold;
  const isLow = balance > 0 && balance <= threshold;
  const isZero = balance <= 0;
  const balanceWarning = balance < 50;
  const blockedCount = allOrders.filter((o) => o.status === "blocked").length;
  const autoRechargeActive = !!autoRecharge?.enabled;
  const totals = stats?.totals || { orders: 0, total: 0, avg_order: 0, pod: 0, shipping: 0, picking: 0 };
  const hasData = totals.orders > 0;

  const downloadProforma = async (tx) => {
    try {
      const res = await api.get(`/client/proforma-invoices/${tx.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Proforma_n${tx.fic_proforma.number}_Teefarm.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      console.error(e);
      toast.error(t("err_download_doc"));
    }
  };

  return (
    <div className="space-y-6" data-testid="client-dashboard">
      <WelcomeSplash />

      <SetupChecklist balance={balance} />

      {/* Welcome */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-[var(--tf-muted)]">
            <Sparkle size={12} weight="fill" /> {me.client?.company_name || t("dash_your_brand")}
          </div>
          <h1 className="font-display font-black text-3xl md:text-4xl lg:text-5xl tracking-tight mt-2 flex items-center gap-3">
            {t("welcome_back")}{firstName ? `, ${firstName}` : ""}
            <HandWaving size={32} weight="fill" className="text-[#D4A017]" />
          </h1>
          <p className="text-[var(--tf-muted)] mt-1.5 text-sm">
            {t("dash_subtitle")}
          </p>
        </div>
        <div className="hidden md:flex items-center gap-2">
          <Link to="/client/orders" className="tf-btn tf-btn-ghost !bg-white !border-2" data-testid="quick-orders">
            <Package size={16} weight="fill" /> {t("orders")}
          </Link>
        </div>
      </div>

      {/* Low-credit alert */}
      {(isLow || isZero) && (
        <div className={`tf-card p-5 ${isZero ? "border-2 border-[var(--tf-error)]" : "border-2 border-[#D4A017]"} flex flex-wrap items-center gap-4`} data-testid="low-credit-alert">
          <div className={`tf-chip ${isZero ? "tf-chip-rose" : "tf-chip-peach"}`}>
            <Warning size={20} weight="fill" />
          </div>
          <div className="flex-1 min-w-[220px]">
            <div className="font-display font-bold">{t("low_credit_warning")}</div>
            <div className="text-sm text-[var(--tf-muted)]">{isZero ? t("zero_credit_message") : t("low_credit_message")}</div>
          </div>
          <Link to="/client/recharge" className="tf-btn tf-btn-primary" data-testid="alert-topup-btn">
            {t("topup_now")} <ArrowRight size={14} weight="bold" />
          </Link>
        </div>
      )}

      {/* ========================= STATUS WIDGETS (3 CARDS) ========================= */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="status-widgets">
        {/* Card 1: Saldo Disponibile */}
        <div
          className={`tf-card p-5 md:p-6 relative overflow-hidden ${balanceWarning ? "border-2 border-[#D4A017]" : ""}`}
          data-testid="widget-balance"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-[var(--tf-muted)] font-semibold">
              <Wallet size={12} weight="fill" /> {t("current_balance")}
            </div>
            {balanceWarning && (
              <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-[#A54B0F]">
                <Warning size={12} weight="fill" /> {t("dash_attention")}
              </span>
            )}
          </div>
          <div
            className="font-display font-black tracking-tight text-4xl md:text-5xl mt-3 tf-numeric text-[var(--tf-ink)]"
            data-testid="wallet-balance"
            style={{ textShadow: "none", WebkitTextStroke: 0 }}
          >
            {fmtEur(balance)}
          </div>
          <div className="text-[11px] text-[var(--tf-muted)] mt-2">
            {t("dash_min_threshold")} <span className="tf-numeric font-bold text-[var(--tf-ink)]">{fmtEur(threshold)}</span>
          </div>
        </div>

        {/* Card 2: Ordini in Attesa */}
        <div className="tf-card p-5 md:p-6" data-testid="widget-blocked">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-[var(--tf-muted)] font-semibold">
            <Warning size={12} weight="fill" /> {t("dash_pending_orders")}
          </div>
          <div className="flex items-end justify-between mt-3">
            <div
              className={`font-display font-black tracking-tight text-4xl md:text-5xl tf-numeric ${blockedCount > 0 ? "text-[var(--tf-error)]" : "text-[var(--tf-ink)]"}`}
              style={{ textShadow: "none", WebkitTextStroke: 0 }}
            >
              {blockedCount}
            </div>
            {blockedCount > 0 && (
              <Link
                to="/client/orders"
                className="text-[11px] text-[var(--tf-ink)] font-bold hover:underline inline-flex items-center gap-1"
                data-testid="goto-blocked"
              >
                {t("common_view")} <ArrowRight size={12} weight="bold" />
              </Link>
            )}
          </div>
          <div className="text-[11px] text-[var(--tf-muted)] mt-2">
            {blockedCount === 0 ? t("dash_no_blocked") : t("dash_blocked_reason")}
          </div>
        </div>

        {/* Card 3: Auto-ricarica */}
        <Link
          to="/client/recharge?tab=auto"
          className="tf-card p-5 md:p-6 hover:border-[var(--tf-ink)] transition-colors block group"
          data-testid="widget-autorecharge"
        >
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-[var(--tf-muted)] font-semibold">
            <Lightning size={12} weight="fill" /> {t("dash_autorecharge")}
          </div>
          <div className="flex items-center gap-3 mt-3">
            <div
              className={`w-11 h-6 rounded-full relative transition-colors ${autoRechargeActive ? "bg-[var(--tf-primary)]" : "bg-[var(--tf-border)]"}`}
              aria-hidden="true"
            >
              <span
                className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-all ${autoRechargeActive ? "left-[22px]" : "left-0.5"}`}
              />
            </div>
            <div
              className={`font-display font-black text-2xl md:text-3xl tf-numeric ${autoRechargeActive ? "text-[var(--tf-success)]" : "text-[var(--tf-muted)]"}`}
              style={{ textShadow: "none", WebkitTextStroke: 0 }}
            >
              {autoRechargeActive ? t("common_active") : t("common_inactive")}
            </div>
          </div>
          <div className="text-[11px] text-[var(--tf-muted)] mt-2 flex items-center gap-1">
            {autoRechargeActive ? t("dash_autorecharge_configure") : t("dash_autorecharge_cta")}
            <ArrowRight size={11} weight="bold" className="group-hover:translate-x-0.5 transition-transform" />
          </div>
        </Link>
      </div>

      {/* ========================= QUICK TOP-UP ========================= */}
      <div className="tf-card p-5 md:p-6" data-testid="quick-topup">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-[var(--tf-muted)] font-semibold mb-3">
          <Lightning size={12} weight="fill" /> {t("dash_quick_topup")}
        </div>
        <div className="grid grid-cols-3 gap-2 md:gap-3">
          {[50, 100, 250].map((amt) => (
            <button
              key={amt}
              onClick={() => quickTopUp(amt)}
              disabled={topupBusy}
              className="rounded-2xl border-2 border-[var(--tf-ink)] bg-white hover:bg-[var(--tf-primary)] hover:border-[var(--tf-primary)] disabled:opacity-50 disabled:cursor-not-allowed transition-all py-3.5 px-3 flex flex-col items-center justify-center font-display font-black text-lg md:text-xl tf-numeric text-[var(--tf-ink)] active:scale-95"
              data-testid={`quick-topup-${amt}`}
              style={{ padding: "14px 12px" }}
            >
              €{amt}
            </button>
          ))}
        </div>
        <form
          onSubmit={(e) => { e.preventDefault(); quickTopUp(customAmount); }}
          className="mt-3 flex gap-2 items-stretch"
        >
          <div className="relative flex-1 min-w-0">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--tf-muted)] font-display font-bold text-sm pointer-events-none">€</span>
            <input
              type="number"
              inputMode="decimal"
              min="50"
              step="1"
              value={customAmount}
              onChange={(e) => setCustomAmount(e.target.value)}
              placeholder={t("dash_other_amount")}
              className="w-full pl-8 pr-3 py-3.5 rounded-2xl border border-[var(--tf-border)] bg-white text-sm font-display font-bold text-[var(--tf-ink)] tf-numeric focus:outline-none focus:border-[var(--tf-ink)] focus:ring-2 focus:ring-[var(--tf-primary)]/30"
              data-testid="quick-topup-custom-input"
              style={{ padding: "14px 12px 14px 32px" }}
            />
          </div>
          <button
            type="submit"
            disabled={topupBusy || !customAmount || parseFloat(customAmount) < 50}
            className="tf-btn tf-btn-primary flex-shrink-0"
            data-testid="quick-topup-custom-submit"
            style={{ padding: "14px 20px" }}
          >
            <CreditCard size={16} weight="bold" />
            <span className="hidden sm:inline">{t("common_pay")}</span>
          </button>
        </form>
        <div className="text-[10px] text-[var(--tf-muted)] mt-2 flex items-center gap-1.5">
          <ShieldCheck size={11} weight="fill" /> {t("dash_secure_payment_note")}
        </div>
      </div>

      {/* Period selector + activity chart + composition */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-[var(--tf-muted)]">
            <TrendUp size={12} weight="fill" /> {t("dash_analysis")}
          </div>
          <h2 className="font-display font-black text-2xl mt-1">{t("dash_how_going")}</h2>
        </div>
        <div className="inline-flex p-1 rounded-full bg-white border border-[var(--tf-border)]">
          {[7, 30, 90].map((d) => (
            <button
              key={d}
              onClick={() => setPeriod(d)}
              data-testid={`period-${d}`}
              className={`px-4 py-1.5 text-xs font-bold rounded-full transition-all ${
                period === d
                  ? "bg-[var(--tf-ink)] text-white"
                  : "text-[var(--tf-muted)] hover:text-[var(--tf-ink)]"
              }`}
            >
              {d === 7 ? t("dash_days_7") : d === 30 ? t("dash_days_30") : t("dash_days_90")}
            </button>
          ))}
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        {/* Daily activity — bar chart with highlight */}
        <div className="tf-card p-6 lg:col-span-2">
          <div className="flex items-start justify-between mb-4">
            <div>
              <div className="font-display font-bold text-lg">{t("dash_daily_spend")}</div>
              {topBar ? (
                <div className="text-xs text-[var(--tf-muted)] mt-1 flex items-center gap-1.5">
                  <TrendUp size={12} weight="bold" className="text-[var(--tf-success)]" />
                  <span className="text-[var(--tf-success)] font-bold">{t("dash_peak")}</span>
                  <span>{topBar.day} · {fmtEur(topBar.value)}</span>
                </div>
              ) : (
                <div className="text-xs text-[var(--tf-muted)] mt-1">{t("dash_no_orders_period")}</div>
              )}
            </div>
            <div className="text-[10px] uppercase tracking-widest text-[var(--tf-muted)]">{t("dash_last_7_days")}</div>
          </div>
          {daily7.length ? (
            <ResponsiveContainer width="100%" height={230}>
              <BarChart data={daily7} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--tf-border)" vertical={false} />
                <XAxis dataKey="day" tick={{ fontSize: 11, fill: "var(--tf-muted)" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 10, fill: "var(--tf-muted)" }} axisLine={false} tickLine={false} />
                <Tooltip
                  cursor={{ fill: "rgba(25,53,41,0.04)" }}
                  content={ChartBarTooltip}
                />
                <Bar dataKey="value" radius={[10, 10, 4, 4]} maxBarSize={38}>
                  {daily7.map((entry, i) => (
                    <Cell key={i} fill={topBar && entry.day === topBar.day && entry.value > 0 ? COLORS.pod : COLORS.shipping} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[230px] flex items-center justify-center text-sm text-[var(--tf-muted)]">
              {t("dash_no_data_period")}
            </div>
          )}
        </div>

        {/* Composition donut */}
        <div className="tf-card p-6">
          <div className="flex items-start justify-between mb-4">
            <div>
              <div className="font-display font-bold text-lg">{t("dash_budget_where")}</div>
              <div className="text-xs text-[var(--tf-muted)] mt-1">{t("dash_budget_sub")}</div>
            </div>
            <ChartPieSlice size={18} weight="fill" className="text-[var(--tf-muted)]" />
          </div>
          {hasData ? (
            <>
              <ResponsiveContainer width="100%" height={160}>
                <PieChart>
                  <Pie
                    data={[
                      { name: "POD", value: totals.pod, color: COLORS.pod },
                      { name: t("shipping"), value: totals.shipping, color: COLORS.shipping },
                      { name: t("dash_picking"), value: totals.picking, color: COLORS.picking },
                    ].filter((s) => s.value > 0)}
                    dataKey="value"
                    nameKey="name"
                    innerRadius={50}
                    outerRadius={75}
                    paddingAngle={3}
                    cornerRadius={4}
                  >
                    {[COLORS.pod, COLORS.shipping, COLORS.picking].map((c, i) => <Cell key={i} fill={c} />)}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-2 mt-3">
                <LegendRow color={COLORS.pod}      label="POD"             value={totals.pod}      total={totals.total} icon={Handbag} />
                <LegendRow color={COLORS.shipping} label={t("shipping")}   value={totals.shipping} total={totals.total} icon={Truck} />
                <LegendRow color={COLORS.picking}  label={t("dash_picking")} value={totals.picking}  total={totals.total} icon={Package} />
              </div>
            </>
          ) : (
            <div className="h-[160px] flex items-center justify-center text-sm text-[var(--tf-muted)]">
              {t("dash_no_spend")}
            </div>
          )}
        </div>
      </div>

      {/* Top products / destinations */}
      {hasData && (
        <div className="grid lg:grid-cols-2 gap-4">
          <div className="tf-card p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="font-display font-bold text-lg">{t("dash_bestsellers")}</div>
                <div className="text-xs text-[var(--tf-muted)] mt-1">{t("dash_bestsellers_sub")}</div>
              </div>
              <div className="tf-chip tf-chip-lavender"><Package size={18} weight="fill" /></div>
            </div>
            {stats.top_products.length ? (
              <div className="space-y-2.5">
                {stats.top_products.slice(0, 5).map((p, i) => {
                  const max = stats.top_products[0].revenue || 1;
                  const w = (p.revenue / max) * 100;
                  return (
                    <div key={i} className="text-xs" data-testid={`bestseller-${i}`}>
                      <div className="flex justify-between mb-1">
                        <span className="truncate pr-2">{p.label}</span>
                        <span className="tf-numeric font-bold">{fmtEur(p.revenue)}</span>
                      </div>
                      <div className="h-2 rounded-full bg-[var(--tf-bg)] overflow-hidden">
                        <div className="h-full rounded-full bg-[var(--tf-primary)]" style={{ width: `${w}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : <div className="text-sm text-[var(--tf-muted)] py-6 text-center">{t("dash_no_products")}</div>}
          </div>

          <div className="tf-card p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="font-display font-bold text-lg">{t("dash_top_destinations")}</div>
                <div className="text-xs text-[var(--tf-muted)] mt-1">{t("dash_top_destinations_sub")}</div>
              </div>
              <div className="tf-chip tf-chip-sky"><Globe size={18} weight="fill" /></div>
            </div>
            {stats.top_countries.length ? (
              <div className="space-y-2.5">
                {stats.top_countries.slice(0, 5).map((c, i) => {
                  const max = stats.top_countries[0].orders || 1;
                  const w = (c.orders / max) * 100;
                  return (
                    <div key={c.country} className="text-xs flex items-center gap-3" data-testid={`dest-${c.country}`}>
                      <span className="w-8 font-bold tf-numeric">{c.country}</span>
                      <div className="flex-1 h-2 rounded-full bg-[var(--tf-bg)] overflow-hidden">
                        <div className="h-full rounded-full bg-[var(--tf-ink)]" style={{ width: `${w}%` }} />
                      </div>
                      <span className="tf-numeric font-bold">{c.orders}</span>
                    </div>
                  );
                })}
              </div>
            ) : <div className="text-sm text-[var(--tf-muted)] py-6 text-center">{t("dash_no_destinations")}</div>}
          </div>
        </div>
      )}

      {/* Achievements — traguardi giocosi */}
      <Achievements me={me} allOrders={allOrders} />

      {/* ========================= UNIFIED ACTIVITY FEED ========================= */}
      <div className="tf-card overflow-hidden" data-testid="activity-feed">
        <div className="flex items-center justify-between p-5 pb-3 gap-3 flex-wrap">
          <div>
            <div className="font-display font-bold text-lg">{t("feed_title")}</div>
            <div className="text-xs text-[var(--tf-muted)] mt-0.5">{t("feed_subtitle")}</div>
          </div>
          <div className="inline-flex p-1 rounded-full bg-[var(--tf-bg)] border border-[var(--tf-border)]" data-testid="feed-filters">
            <button
              onClick={() => setFeedFilter("all")}
              className={`px-3 md:px-4 py-1.5 text-xs font-bold rounded-full transition-all ${
                feedFilter === "all"
                  ? "bg-[var(--tf-ink)] text-white"
                  : "text-[var(--tf-muted)] hover:text-[var(--tf-ink)]"
              }`}
              data-testid="feed-filter-all"
            >
              {t("all")}
            </button>
            <button
              onClick={() => setFeedFilter("recharges")}
              className={`px-3 md:px-4 py-1.5 text-xs font-bold rounded-full transition-all ${
                feedFilter === "recharges"
                  ? "bg-[var(--tf-ink)] text-white"
                  : "text-[var(--tf-muted)] hover:text-[var(--tf-ink)]"
              }`}
              data-testid="feed-filter-recharges"
            >
              {t("feed_only_recharges")}
            </button>
          </div>
        </div>

        <div className="border-t border-[var(--tf-border)] divide-y divide-[var(--tf-border)]">
          {unifiedFeed.length === 0 && (
            <div className="p-8 text-center text-[var(--tf-muted)] text-sm">
              {feedFilter === "recharges" ? t("feed_no_recharges") : t("empty")}
            </div>
          )}
          {unifiedFeed.map((item) => {
            const isCredit = item.direction === "credit";
            const isAuto = item.kind === "auto_recharge";
            const isBlocked = item.kind === "order" && item.status === "blocked";
            const Icon = isAuto ? Lightning : isCredit ? Plus : Package;
            const iconBg = isAuto
              ? "#ECFCC9"
              : isCredit
              ? "#D1F0E0"
              : isBlocked
              ? "#FBDDE1"
              : "#F5F2E8";
            const iconColor = isAuto
              ? "#3E5A0A"
              : isCredit
              ? "#1F6E4A"
              : isBlocked
              ? "#A11534"
              : "#193529";
            return (
              <div
                key={item.key}
                className="flex items-center gap-3 md:gap-4 p-4 md:px-5"
                data-testid={`feed-item-${item.key}`}
              >
                <div
                  className="w-11 h-11 md:w-12 md:h-12 rounded-2xl flex items-center justify-center flex-shrink-0"
                  style={{ background: iconBg, color: iconColor }}
                >
                  <Icon size={20} weight="fill" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-display font-bold text-sm truncate">
                    {isCredit
                      ? isAuto ? t("feed_auto_recharge") : t("feed_wallet_recharge")
                      : item.label}
                    {isBlocked && (
                      <span className="ml-2 tf-status-pill tf-status-error text-[9px] align-middle">{t("blocked")}</span>
                    )}
                  </div>
                  <div className="text-[11px] text-[var(--tf-muted)] mt-0.5 flex items-center gap-1.5 flex-wrap">
                    <Calendar size={11} weight="bold" />
                    <span>{fmtDate(item.date, lang)}</span>
                    {item.subtitle && (
                      <>
                        <span className="opacity-40">·</span>
                        <span className="truncate">{item.subtitle}</span>
                      </>
                    )}
                    {isCredit && item.tx?.fic_proforma?.pdf_url && (
                      <>
                        <span className="opacity-40">·</span>
                        <button
                          onClick={() => downloadProforma(item.tx)}
                          className="inline-flex items-center gap-1 text-[var(--tf-ink)] font-semibold hover:underline"
                          data-testid={`feed-pdf-${item.key}`}
                        >
                          <Receipt size={11} weight="fill" /> n.{item.tx.fic_proforma.number}
                          <DownloadSimple size={10} weight="bold" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
                <div
                  className={`font-display font-black text-sm md:text-base tf-numeric flex-shrink-0 text-right ${
                    isCredit ? "text-[var(--tf-ink)]" : "text-[#C7476A]"
                  }`}
                  style={{ textShadow: "none", WebkitTextStroke: 0 }}
                >
                  {isCredit ? "+" : "−"}{fmtEur(item.amount)}
                </div>
              </div>
            );
          })}
        </div>
        <div className="p-3 border-t border-[var(--tf-border)] flex justify-center gap-4 text-xs">
          <Link to="/client/orders" className="text-[var(--tf-muted)] hover:text-[var(--tf-ink)] font-semibold inline-flex items-center gap-1" data-testid="goto-orders">
            <Package size={12} weight="bold" /> {t("feed_all_orders")}
          </Link>
          <span className="opacity-30">·</span>
          <Link to="/client/transactions" className="text-[var(--tf-muted)] hover:text-[var(--tf-ink)] font-semibold inline-flex items-center gap-1" data-testid="goto-txs">
            <ArrowRight size={12} weight="bold" /> {t("feed_all_transactions")}
          </Link>
        </div>
      </div>
    </div>
  );
}

function MiniKPI({ chip, icon: Icon, label, value }) {
  return (
    <div className="tf-card p-5 flex items-center gap-4">
      <div className={`tf-chip ${chip}`}>
        <Icon size={20} weight="fill" />
      </div>
      <div className="min-w-0">
        <div className="text-[11px] uppercase tracking-widest text-[var(--tf-muted)]">{label}</div>
        <div className="font-display font-black text-xl tf-numeric truncate">{value}</div>
      </div>
    </div>
  );
}

function LegendRow({ color, label, value, total, icon: Icon }) {
  const p = total ? Math.round((value / total) * 100) : 0;
  return (
    <div className="flex items-center justify-between text-xs">
      <span className="flex items-center gap-2">
        <span className="w-2.5 h-2.5 rounded-full" style={{ background: color }} />
        <Icon size={12} weight="fill" className="text-[var(--tf-muted)]" />
        <span>{label}</span>
      </span>
      <span className="tf-numeric">
        <b>{fmtEur(value)}</b> <span className="text-[var(--tf-muted)]">({p}%)</span>
      </span>
    </div>
  );
}


/* ---------------- Achievement Badges ---------------- */
function Achievements({ me, allOrders }) {
  const { t } = useI18n();
  const orderCount = allOrders?.length || 0;
  const blockedCount = allOrders?.filter((o) => o.status === "blocked").length || 0;
  const createdAt = me?.client?.created_at ? new Date(me.client.created_at) : null;
  const monthsSince = createdAt ? Math.max(0, Math.floor((Date.now() - createdAt.getTime()) / (1000 * 60 * 60 * 24 * 30))) : 0;
  const plan = me?.client?.subscription_plan || "start";

  const badges = [
    {
      key: "first-order",
      title: t("ach_first_order_title"),
      subtitle: t("ach_first_order_sub"),
      icon: Star,
      chip: "tf-chip-lime",
      unlocked: orderCount >= 1,
      progress: Math.min(1, orderCount / 1),
    },
    {
      key: "ten-orders",
      title: t("ach_ten_orders_title"),
      subtitle: t("ach_ten_orders_sub"),
      icon: Fire,
      chip: "tf-chip-peach",
      unlocked: orderCount >= 10,
      progress: Math.min(1, orderCount / 10),
      progressLabel: `${Math.min(orderCount, 10)} / 10`,
    },
    {
      key: "hundred-orders",
      title: t("ach_hundred_orders_title"),
      subtitle: t("ach_hundred_orders_sub"),
      icon: Trophy,
      chip: "tf-chip-lavender",
      unlocked: orderCount >= 100,
      progress: Math.min(1, orderCount / 100),
      progressLabel: `${Math.min(orderCount, 100)} / 100`,
    },
    {
      key: "always-green",
      title: t("ach_always_green_title"),
      subtitle: blockedCount === 0 ? t("ach_always_green_sub_zero") : t("ach_always_green_sub_n", { n: blockedCount }),
      icon: ShieldCheck,
      chip: "tf-chip-mint",
      unlocked: orderCount > 0 && blockedCount === 0,
    },
    {
      key: "veteran",
      title: monthsSince >= 12 ? t("ach_veteran_title_done") : t("ach_veteran_title_pending"),
      subtitle: monthsSince >= 12 ? t("ach_veteran_sub_done", { n: monthsSince }) : t("ach_veteran_sub_pending", { n: monthsSince }),
      icon: Medal,
      chip: "tf-chip-sky",
      unlocked: monthsSince >= 12,
      progress: Math.min(1, monthsSince / 12),
    },
    {
      key: "elite",
      title: plan === "elite" ? t("ach_elite_title_done") : t("ach_elite_title_pending"),
      subtitle: plan === "elite" ? t("ach_elite_sub_done") : t("ach_elite_sub_pending"),
      icon: Crown,
      chip: "tf-chip-rose",
      unlocked: plan === "elite",
    },
  ];

  const unlockedCount = badges.filter((b) => b.unlocked).length;

  // Celebrate any freshly unlocked badges (persists to localStorage)
  useEffect(() => { celebrateNewlyUnlocked(badges); }, [me?.client?.id, orderCount, blockedCount, plan, badges]);

  return (
    <div className="tf-card p-6" data-testid="achievements">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-5">
        <div className="flex items-start gap-3">
          <div className="tf-chip tf-chip-lime">
            <Confetti size={20} weight="fill" />
          </div>
          <div>
            <div className="font-display font-bold text-lg">{t("ach_title")}</div>
            <div className="text-xs text-[var(--tf-muted)] mt-0.5">
              {t("ach_unlocked_of", { n: unlockedCount, total: badges.length })}
            </div>
          </div>
        </div>
        <div className="flex-1 max-w-[220px] hidden sm:block">
          <div className="h-2 rounded-full bg-[var(--tf-bg)] overflow-hidden">
            <div
              className="h-full rounded-full bg-[var(--tf-primary)] transition-all duration-500"
              style={{ width: `${(unlockedCount / badges.length) * 100}%` }}
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {badges.map((b) => {
          const Icon = b.icon;
          return (
            <div
              key={b.key}
              data-testid={`badge-${b.key}`}
              className={`relative rounded-2xl border-2 p-4 transition-all ${
                b.unlocked
                  ? "border-[var(--tf-primary)] bg-white"
                  : "border-dashed border-[var(--tf-border)] bg-[var(--tf-bg)]/40"
              }`}
            >
              <div className={`tf-chip ${b.chip} ${b.unlocked ? "" : "opacity-40 grayscale"} mb-3`}>
                <Icon size={20} weight="fill" />
              </div>
              {!b.unlocked && (
                <div className="absolute top-3 right-3 text-[var(--tf-muted)]">
                  <Lock size={12} weight="bold" />
                </div>
              )}
              <div className={`font-display font-bold text-sm leading-tight ${b.unlocked ? "text-[var(--tf-ink)]" : "text-[var(--tf-muted)]"}`}>
                {b.title}
              </div>
              <div className="text-[10px] text-[var(--tf-muted)] mt-1 leading-snug">{b.subtitle}</div>
              {!b.unlocked && b.progress != null && (
                <div className="mt-3">
                  <div className="h-1 rounded-full bg-[var(--tf-bg)] overflow-hidden">
                    <div
                      className="h-full rounded-full bg-[var(--tf-ink)]/40"
                      style={{ width: `${b.progress * 100}%` }}
                    />
                  </div>
                  {b.progressLabel && (
                    <div className="text-[9px] text-[var(--tf-muted)] tf-numeric mt-1">{b.progressLabel}</div>
                  )}
                </div>
              )}
              {b.unlocked && (
                <div className="mt-2 text-[10px] font-bold text-[var(--tf-success)] uppercase tracking-widest">
                  ✓ {t("ach_unlocked_badge")}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
